import React, { type PropsWithChildren, useCallback, useRef } from 'react';
import { StyleSheet, View, type ViewStyle } from 'react-native';

import { theme } from '@garnish/constants';

import { useBrowserEventListener, useHovered } from '../../../../hooks';
import { webOnlyStyles } from '../../../../utils';

// ─────────────────────────────────────────────────────────────────────────────

export const IngredientCardContainer = (props: Props) => {
  const { style, children, isMuted, isHoverDisabled, dataSet, onKeyDown, onHoverIn, onHoverOut } =
    props;

  const ref = useRef(null);
  const isHovered = useHovered(ref) && !isHoverDisabled;

  // ─── Helpers ─────────────────────────────────────────────────────────

  const handleKeyDown = useCallback(
    (event: Event) => {
      const castedEvent = event as KeyboardEvent;
      const castedRef = ref.current as unknown as HTMLElement;
      const castedTarget = event.target as Node;
      const isValidTarget = castedTarget === castedRef || castedRef?.contains?.(castedTarget);

      if (!isValidTarget) {
        return;
      }

      onKeyDown?.(castedEvent);
    },
    [onKeyDown],
  );

  const handleMouseEnter = useCallback(() => {
    onHoverIn?.();
  }, [onHoverIn]);

  const handleMouseLeave = useCallback(() => {
    onHoverOut?.();
  }, [onHoverOut]);

  // ─── Effects ─────────────────────────────────────────────────────────

  useBrowserEventListener(ref, 'keydown', handleKeyDown);
  useBrowserEventListener(ref, 'mouseenter', handleMouseEnter);
  useBrowserEventListener(ref, 'mouseleave', handleMouseLeave);

  // ─────────────────────────────────────────────────────────────────────

  const containerStyles = [
    styles.container,
    isHovered ? styles.hover : undefined,
    isMuted ? styles.muted : undefined,
    containerWebStyles,
    style,
  ];

  return (
    <View ref={ref} style={containerStyles} {...(dataSet ? { dataSet } : {})}>
      {children}
    </View>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    gap: theme.spacing['2'],
  },
  hover: {
    opacity: 0.8,
  },
  muted: {
    opacity: 0.5,
  },
});

const containerWebStyles = webOnlyStyles({
  transition: theme.transitions.presets.colorsAndOpacity,
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = PropsWithChildren<{
  style?: ViewStyle;
  isMuted?: boolean;
  isHoverDisabled?: boolean;
  dataSet?: Record<string, string | number>;
  onKeyDown?: (event: KeyboardEvent) => void;
  onHoverIn?: () => void;
  onHoverOut?: () => void;
}>;
